/**
 * Revenue Stats family drill-down (WSRADM-0022 / WSRADM-0034).
 *
 * By product / By member toggle lives in `?view=`. Currency and dimension live
 * in the URL and are forwarded on every fetch and in-app link. Catalogue links
 * use `inCatalogue` only — `productKey` is an opaque identity, never parsed.
 * Walk-in is labelled and not linked. Unmapped resolves via path helpers.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Link,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatMonthLong } from '@/api/reports/format';
import {
  appendReportParams,
  familySlugToPath,
  fetchRevenueMembers,
  fetchRevenueMonth,
  fetchRevenueProducts,
  isCurrencyNotFound,
  isDimensionNotFound,
  pathToFamilySlug,
  rebuildReports,
} from '@/api/reports/reportsApi';
import type { RevenueMemberRow, RevenueProductRow } from '@/api/reports/types';
import { useAuth } from '@/auth/AuthContext';
import { canRebuildReports } from '@/auth/reportAccess';
import { ReportFreshness } from '@/components/ReportFreshness';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH } from '@/components/rosterFormat';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

const PRODUCT_SORT_ALLOWLIST = new Set([
  'revenue',
  'productName',
  'distinctMembers',
  'unitCount',
]);

const MEMBER_SORT_ALLOWLIST = new Set(['revenue', 'memberName', 'lineCount', 'unitCount']);

type FamilyView = 'product' | 'member';

function parseView(raw: string | null): FamilyView {
  return raw === 'member' ? 'member' : 'product';
}

export function RevenueFamilyPage() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const { operator } = useAuth();
  const { month = '', familySlug: familyToken = '' } = useParams<{
    month: string;
    familySlug: string;
  }>();
  const familySlug = pathToFamilySlug(familyToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get('view'));
  const dimension = searchParams.get('dimension') ?? '';
  const currency = searchParams.get('currency') ?? '';

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState<'ok' | 'error' | null>(null);
  const [rebuildError, setRebuildError] = useState<unknown>(null);
  // Keep the last notice kind so the snackbar content does not flip to the success
  // copy while an error is transitioning out after being dismissed.
  const [lastNotice, setLastNotice] = useState<'ok' | 'error'>('ok');
  if (rebuildNotice !== null && rebuildNotice !== lastNotice) setLastNotice(rebuildNotice);
  const shownNotice = rebuildNotice ?? lastNotice;

  const header = useResource(
    () => fetchRevenueMonth(month, { dimension, currency }),
    [month, dimension, currency],
  );
  const listQuery = {
    currency: header.data?.currency ?? '',
    dimension,
    search,
    sort,
    order,
    page,
    size,
  };
  const products = useResource(
    () => fetchRevenueProducts(month, familySlug, listQuery),
    [month, familyToken, header.data?.currency, dimension, search, sort, order, page, size],
  );
  const members = useResource(
    () => fetchRevenueMembers(month, familySlug, listQuery),
    [month, familyToken, header.data?.currency, dimension, search, sort, order, page, size],
  );

  const canRebuild = canRebuildReports(operator?.roles);
  const na = t('reports.revenue.na', 'N/A');
  const walkInLabel = t('reports.revenue.walkIn', 'Walk-in');
  const responseCurrency = header.data?.currency ?? '';
  const familyName =
    header.data?.familyColumns.find((column) => column.slug === familySlug)?.displayName ??
    familyToken;
  useDocumentTitle(familyName);
  const sep = t('reports.revenue.breadcrumb.separator', '›');
  const monthLabel = formatMonthLong(month, i18n.language);
  const reportParams = { dimension, currency: responseCurrency || currency };

  useEffect(() => {
    if (header.status !== 'error') return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (isDimensionNotFound(header.error) && dimension !== '') {
      next.delete('dimension');
      changed = true;
    } else if (isCurrencyNotFound(header.error) && currency !== '') {
      next.delete('currency');
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [header.status, header.error, dimension, currency, searchParams, setSearchParams]);

  const handleRebuild = () => {
    setRebuilding(true);
    void rebuildReports()
      .then(() => setRebuildNotice('ok'))
      .catch((err: unknown) => {
        setRebuildError(err);
        setRebuildNotice('error');
      })
      .finally(() => setRebuilding(false));
  };

  const setView = (nextView: FamilyView) => {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'product') next.delete('view');
    else next.set('view', nextView);
    setSearchParams(next);
    setPage(0);
    setSort('');
    setOrder('desc');
  };

  const fmt = (amount: number) => formatMoney(amount, responseCurrency, i18n.language);
  const linesHref = (productKey: string) =>
    appendReportParams(
      `/reports/revenue/${month}/${familySlugToPath(familySlug)}/lines?productKey=${encodeURIComponent(productKey)}`,
      reportParams,
    );

  const productColumns = useMemo<RosterColumn<RevenueProductRow>[]>(
    () => [
      {
        id: 'productName',
        header: t('reports.revenue.products.product', 'Product'),
        sortId: 'productName',
        render: (row) => {
          const name = row.productName ?? EM_DASH;
          if (row.inCatalogue) {
            return (
              <Link component={RouterLink} to="/products">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        id: 'revenue',
        header: t('reports.revenue.products.revenue', 'Revenue'),
        sortId: 'revenue',
        defaultOrder: 'desc',
        render: (row) => (
          <Link component={RouterLink} to={linesHref(row.productKey)}>
            {fmt(row.revenue)}
          </Link>
        ),
      },
      {
        id: 'lineCount',
        header: t('reports.revenue.products.lines', 'Lines'),
        render: (row) => String(row.lineCount),
      },
      {
        id: 'unitCount',
        header: t('reports.revenue.products.units', 'Units'),
        sortId: 'unitCount',
        defaultOrder: 'desc',
        render: (row) => String(row.unitCount),
      },
      {
        id: 'distinctMembers',
        header: t('reports.revenue.products.members', 'Members'),
        sortId: 'distinctMembers',
        defaultOrder: 'desc',
        render: (row) => String(row.distinctMembers),
      },
      {
        id: 'average',
        header: t('reports.revenue.products.average', 'Average'),
        render: (row) => (row.average === null ? na : fmt(row.average)),
      },
    ],
    [currency, dimension, familySlug, i18n.language, month, na, responseCurrency, t],
  );

  const memberColumns = useMemo<RosterColumn<RevenueMemberRow>[]>(
    () => [
      {
        id: 'memberName',
        header: t('reports.revenue.members.member', 'Member'),
        sortId: 'memberName',
        render: (row) => {
          if (row.walkIn) return walkInLabel;
          if (row.sourceClientId === null) return row.memberName ?? EM_DASH;
          return (
            <Link component={RouterLink} to={`/users/${row.sourceClientId}`}>
              {row.memberName ?? EM_DASH}
            </Link>
          );
        },
      },
      {
        id: 'lineCount',
        header: t('reports.revenue.members.lines', 'Lines'),
        sortId: 'lineCount',
        defaultOrder: 'desc',
        render: (row) => String(row.lineCount),
      },
      {
        id: 'unitCount',
        header: t('reports.revenue.members.units', 'Units'),
        sortId: 'unitCount',
        defaultOrder: 'desc',
        render: (row) => String(row.unitCount),
      },
      {
        id: 'revenue',
        header: t('reports.revenue.members.revenue', 'Revenue'),
        sortId: 'revenue',
        defaultOrder: 'desc',
        render: (row) => fmt(row.revenue),
      },
    ],
    [i18n.language, responseCurrency, t, walkInLabel],
  );

  const neverComputed = header.status === 'ready' && header.data?.neverComputed === true;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        <Link
          component={RouterLink}
          to={appendReportParams('/reports/revenue', reportParams)}
        >
          {t('reports.revenue.breadcrumb.revenue', 'Revenue')}
        </Link>
        {` ${sep} ${monthLabel} ${sep} ${familyName}`}
      </Typography>

      <Typography variant="h1" sx={{ mb: 1 }}>
        {familyName}
      </Typography>
      {responseCurrency.length > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {t('reports.revenue.currencyCaption', 'Currency: {{code}}', {
            code: responseCurrency,
          })}
        </Typography>
      ) : (
        <Box sx={{ mb: 2.5 }} />
      )}

      {header.data ? (
        <ReportFreshness
          asOf={header.data.asOf}
          stale={header.data.stale}
          neverComputed={header.data.neverComputed}
          timezone={header.data.timezone}
          canRebuild={canRebuild}
          onRebuild={handleRebuild}
          rebuilding={rebuilding}
        />
      ) : null}

      <Tabs
        value={view}
        onChange={(_event, value: FamilyView) => setView(value)}
        sx={{ mb: 3 }}
      >
        <Tab
          value="product"
          label={t('reports.revenue.view.product', 'By product')}
        />
        <Tab
          value="member"
          label={t('reports.revenue.view.member', 'By member')}
        />
      </Tabs>

      {neverComputed ? null : view === 'product' ? (
        <RosterTable
          title={t('reports.revenue.products.title', 'Products')}
          columns={productColumns}
          rows={products.data?.content ?? []}
          rowId={(row) => row.productKey}
          status={products.status}
          error={products.error}
          totalElements={products.data?.totalElements ?? 0}
          page={page}
          size={size}
          sort={sort}
          order={order}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setSize(next);
            setPage(0);
          }}
          onSortChange={(id, nextOrder) => {
            if (!PRODUCT_SORT_ALLOWLIST.has(id)) return;
            setSort(id);
            setOrder(nextOrder);
            setPage(0);
          }}
          onApply={(query) => {
            setSearch(query.search);
            setPage(0);
          }}
          onRetry={products.refetch}
        />
      ) : (
        <RosterTable
          title={t('reports.revenue.members.title', 'Members')}
          columns={memberColumns}
          rows={members.data?.content ?? []}
          rowId={(row) =>
            row.walkIn ? 'walk-in' : String(row.sourceClientId ?? row.memberName ?? 'member')
          }
          status={members.status}
          error={members.error}
          totalElements={members.data?.totalElements ?? 0}
          page={page}
          size={size}
          sort={sort}
          order={order}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setSize(next);
            setPage(0);
          }}
          onSortChange={(id, nextOrder) => {
            if (!MEMBER_SORT_ALLOWLIST.has(id)) return;
            setSort(id);
            setOrder(nextOrder);
            setPage(0);
          }}
          onApply={(query) => {
            setSearch(query.search);
            setPage(0);
          }}
          onRetry={members.refetch}
        />
      )}

      <Snackbar
        open={rebuildNotice !== null}
        autoHideDuration={rebuildNotice === 'error' ? null : 6000}
        onClose={(_event, reason) => {
          // A failure stays until dismissed — a click elsewhere must not hide it.
          if (reason === 'clickaway' && rebuildNotice === 'error') return;
          setRebuildNotice(null);
        }}
      >
        <Alert
          severity={shownNotice === 'error' ? 'error' : 'success'}
          onClose={() => setRebuildNotice(null)}
        >
          {shownNotice === 'error' ? (
            <>
              {t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              <ErrorDetails
                error={rebuildError}
                headline={t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              />
            </>
          ) : (
            t(
              'reports.freshness.rebuildQueued',
              'Rebuild queued. Numbers will refresh when the rollup finishes.',
            )
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
}
